// features/category/categorySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import categoryServices from "../../../services/categoryServices";

const initialState = {
  categories: [],
  category: null,
  isLoading: false,
  error: null,
  success: null,
};

// 🟢 Async Thunks

export const createCategory = createAsyncThunk(
  "category/createCategory",
  async (data, thunkAPI) => {
    try {
      return await categoryServices.createCategory(data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getallCategories = createAsyncThunk(
  "category/getallCategories",
  async (thunkAPI) => {
    try {
      console.log("Fetching categories with activeOnly:");
      return await categoryServices.getallCategories();
    } catch (error) {
      console.log("Error fetching categories:", error);
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getSingleCategory = createAsyncThunk(
  "category/getSingleCategory",
  async (id, thunkAPI) => {
    try {
      return await categoryServices.getSingleCategory(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async ({ id, data }, thunkAPI) => {
    try {
      return await categoryServices.updateCategory(id, data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "category/deleteCategory",
  async (id, thunkAPI) => {
    try {
      return await categoryServices.deleteCategory(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const activateCategory = createAsyncThunk(
  "category/activateCategory",
  async (id, thunkAPI) => { 
    try {
      return await categoryServices.activateCategory(id);
    }
    catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🟣 Slice

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    resetCategoryState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = null;
      state.category = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.categories.push(action.payload);
        toast.success("Category created successfully");
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // GET ALL
      .addCase(getallCategories.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getallCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload.data || action.payload;
        console.log("Fetched categories:", state.categories);
      })
      .addCase(getallCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // GET SINGLE
      .addCase(getSingleCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.category = action.payload;
      })
      .addCase(getSingleCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.categories = state.categories.map((cat) =>
          cat._id === action.payload._id ? action.payload : cat
        );
        toast.success("Category updated successfully");
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // DELETE (soft delete)
      .addCase(deleteCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        // mark deleted category as inactive (soft delete)
        state.categories = state.categories.map((cat) =>
          cat._id === action.meta.arg ? { ...cat, isActive: false } : cat
        );
        toast.success("Category deactivated successfully");
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })
      // ACTIVATE
      .addCase(activateCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(activateCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.categories = state.categories.map((cat) =>
          cat._id === action.payload._id ? action.payload : cat
        );
        toast.success("Category activated successfully");
      })
      .addCase(activateCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      });
  },
});

export const { resetCategoryState } = categorySlice.actions;

export const selectCategories = (state) => state.category.categories;
export const selectCategory = (state) => state.category.category;

export default categorySlice.reducer;